import type { Catalog } from '../types';

// Shared chrome: CheckoutShell, StepIndicator, OrderSummary. Server-delivered
// strings (product names, support email, raw billing intervals the catalog
// doesn't know) are NOT here — they render verbatim.
export const shell: Catalog = {
  'shell.close': 'Close',
  // Fragment: the support email renders after it as a mailto link.
  'shell.supportContact': 'Need help? Contact the publisher at',

  'steps.contact': 'Contact',
  'steps.payment': 'Payment',
  'steps.done': 'Done',
  // Fallback label for a step beyond the named ones.
  'steps.generic': 'Step {step}',
  'steps.progress': {
    one: 'Step {n} of {total}',
    other: 'Step {n} of {total}',
  },
  'steps.ariaLabel': {
    one: 'Checkout step {n} of {total}',
    other: 'Checkout step {n} of {total}',
  },

  'orderSummary.ariaLabel': 'Order summary',
  'orderSummary.heading': 'Order summary',
  // Neutral product name until the catalog config loads (or if it failed).
  'orderSummary.defaultProductName': 'Checkout',
  'orderSummary.kind.support': 'Support',
  'orderSummary.kind.subscription': 'Subscription',
  'orderSummary.kind.oneTime': 'One-time purchase',
  'orderSummary.kind.purchase': 'Purchase',
  'orderSummary.subtotal': 'Subtotal',
  'orderSummary.discount': 'Discount',
  'orderSummary.tax': 'Tax',
  'orderSummary.totalDueToday': 'Total due today',
  'orderSummary.calculatedAtCheckout': 'Calculated at checkout',
  'orderSummary.secureNote': 'Secure payment processing by Stripe.',
  'orderSummary.freeTrial': 'Free trial',
  'orderSummary.trialDays': {
    one: '{n} day',
    other: '{n} days',
  },
  'orderSummary.trialThen': {
    one: 'Then {price} after your {n} day free trial. Cancel anytime.',
    other: 'Then {price} after your {n} days free trial. Cancel anytime.',
  },
  'orderSummary.discount.once': 'A {amount} discount will be applied to your first charge.',
  'orderSummary.discount.repeating': 'A {amount} discount will be applied for a limited time.',
  'orderSummary.discount.forever': 'A {amount} discount will be applied to every charge.',
  // Exact month-count form for repeating coupons (every path that has the count
  // asserts it; the vague form above is the no-count fallback).
  'orderSummary.discount.repeatingMonths': {
    one: 'A {amount} discount will be applied for {count} month.',
    other: 'A {amount} discount will be applied for {count} months.',
  },
  // The trial-summary variants: the schedule-backed trial's discount window
  // opens at the transition, so the note says when it starts.
  'orderSummary.discount.onceOnTrial': 'A {amount} discount will be applied to your first charge when your trial ends.',
  'orderSummary.discount.repeatingOnTrial': 'A {amount} discount will be applied for a limited time, starting when your trial ends.',
  'orderSummary.discount.repeatingMonthsOnTrial': {
    one: 'A {amount} discount will be applied for {count} month, starting when your trial ends.',
    other: 'A {amount} discount will be applied for {count} months, starting when your trial ends.',
  },
  'orderSummary.discount.foreverOnTrial': 'A {amount} discount will be applied to every charge, starting when your trial ends.',
  // What later charges cost, under "Total due today"; {price} = "$5.00/month".
  'orderSummary.thenPrice': 'Then {price}.',
  // A discount covering the first N monthly charges: {today} is the discounted
  // monthly amount, {price} the full one, both with cadence.
  'orderSummary.discountedForMonths': {
    one: '{today} for the first month, then {price}.',
    other: '{today} for the first {count} months, then {price}.',
  },
  // Compact cadence suffix on amounts ("$9.99/month"); {interval} is the
  // translated interval noun (interval.* keys in the paywall namespace).
  'orderSummary.cadence': '/{interval}',
};
